type ViewerFavorites = number[]
type TimerState = 'running' | 'finished'

export interface ProjectState {
  channel_url: string
  project: Project
  return_url?: string
  api_host: string
  participant_id: string | null
  viewer_key?: string
  favorites: number[]
  viewer_history: ViewerHistoryEntry[]
  timer?: { state: TimerState; remaining_seconds: number }
  search_query: string | null
  search_query_regex: RegExp | null
  selection: ProjectPath | null
  played_activity: string | null
  description: string
  article_engagements: {
    article_loading: boolean
    engagement_types: string[]
    emojis: {
      active_emoji_id: boolean
      list: any[] | null
    }
  }
  auth_token: string | null
  numberOfChannelActions: number
  numberOfActivityActions: number
  activitiesPlayed: string[]
  tilesClicked: string[]
}

export const RedirectUrlActivityFlag = '_ACTIVITY_ACTIONS_'
export const RedirectUrlChannelFlag = '_CHANNEL_ACTIONS_'
export const RedirectUrlChannelActivitiesPlayedFlag = '_ACTIVITIES_PLAYED_'
export const RedirectUrlChannelTilesClickedFlag = '_TILES_CLICKED_'

export enum AppendToPassbackKind {
  ACTIVITIES_PLAYED,
  TILES_CLICKED,
}

export type RowTileType =
  | 'category'
  | 'dynamic_history'
  | 'dynamic_favorites'
  | 'dynamic_popular'

export interface ViewerHistoryEntry {
  activity_id: number
  session_count: number
  seconds_viewed: number
  percent_viewed: number
}

export interface ProjectQueryStringParams {
  participant_id?: string
  return_url?: string
  reset_session?: boolean
  reset_timer?: boolean
}

interface DataExportLink {
  name: string
  slug: string
}

export interface ProjectActivity {
  id: number
  activity_id: number
  url: string
  name: string
  synopsis: string
  portal: string
  description: null | string
  thumbnail_url: string
  poster_url: string
  last_updated: string
  is_highlighted: boolean
  override_thumbnail_url: null | string
  override_poster_url: null | string
  views: number
  display_state_id: null | number
  categories: number[]
  links: null
  display_position: null | number
}

//An activity is nested in a row, and both rows and activities of them have an index of their position
//within their siblings. It's tempting to call this a cartesian system, but if we consider that another
//class of object could be nested inside an activity, then it's more correct to call this a contour or path on a
//tree, i.e. the Project is the root, the ProjectRows are the top-branches and the ProjectActivities are
//the second-order branches. In this way, a reference to a row and an activity as well as the position
//indices for both form a path on the tree. Currently ProjectActivity are leaves, but future features
//might call for further nesting and in that case, this ProjectPath structure can scale up in complexity,
//but all of the components will still just consume references to a ProjectPath.
export interface ProjectPath {
  activity: ProjectActivity | null
  activity_index: number | null
  row: ProjectRow | null
  row_index: number | null
}

interface ProjectLink {
  id: number
  label: string
  url: string
  order: number
}

type RowType = 'TILES' | 'HERO_CAROUSEL' | 'CATEGORY_MENU' | 'HTML_BLOCK'

export interface ProjectRow {
  id: number
  type: RowType
  row_tile_type: RowTileType
  is_active: boolean
  display_order: number
  layout_class?: string
  layout?: string
  name?: string
  html_code?: string
  is_enabled_title_display: boolean
  is_enabled_article_format: boolean
  is_enabled_activity_clickthrough: boolean
  is_enabled_second_screen: boolean
  is_enabled_pagination_controls?: boolean
  autoplay_speed: number
}
interface PopularActivity {
  activity_id: number
  rank: number
}
export interface Project {
  id: number
  name: string
  intro_headline: string | null
  intro_description: string | null
  primary_color: string
  contrast_color: string
  logo_url: string
  url_slug: string
  project_guid: string
  primary_color_rgb: number[]
  is_active: boolean
  allow_anonymous_contribution: boolean
  min_view_time: null | number
  min_view_message: string
  min_view_redirect_auto: boolean
  min_view_redirect_link: boolean
  disable_activity_clickthrough: boolean
  button_1_label: string
  button_2_label: string
  button_1_icon: string
  button_2_icon: string
  is_enabled_category_display: boolean
  is_enabled_tile_order_randomization: boolean
  is_enabled_search: boolean
  is_enabled_share: boolean
  custom_css: string
  links: Array<ProjectLink>
  activities: Array<ProjectActivity>
  members: []
  data_export_links: DataExportLink[]
  rows: ProjectRow[]
  popular_activities: PopularActivity[]
  social_links: {
    facebook: string | null
    twitter: string | null
  }
}

export const URLParameterRange_ActivityType = {
  ACTIVITY: 'activity',
  ARTICLE: 'article',
}
